// src/components/GovtAirAdvisory.jsx
import React from "react";
import { AlertTriangle, Info } from "lucide-react";

/* ---------------- AQI → GRAP LOGIC ---------------- */

function getGrapStage(rawAqi) {
  const aqi = Number(rawAqi);

  if (!aqi || Number.isNaN(aqi)) return null;

  if (aqi <= 200) {
    return {
      stage: "Normal Conditions",
      level: "Moderate",
      color: "bg-green-100 text-green-700",
      advisory: [
        "Outdoor activities can be continued normally",
        "Sensitive individuals should monitor symptoms",
      ],
    };
  }

  if (aqi <= 300) {
    return {
      stage: "GRAP Stage I–II",
      level: "Poor",
      color: "bg-yellow-100 text-yellow-700",
      advisory: [
        "People with respiratory issues should limit outdoor exertion",
        "Consider improving indoor air quality",
      ],
    };
  }

  if (aqi <= 400) {
    return {
      stage: "GRAP Stage III",
      level: "Very Poor",
      color: "bg-orange-100 text-orange-700",
      advisory: [
        "Avoid prolonged outdoor exposure",
        "Children, elderly & asthma patients should remain indoors",
        "Use indoor air purification where possible",
      ],
    };
  }

  return {
    stage: "GRAP Stage IV",
    level: "Severe",
    color: "bg-red-100 text-red-700",
    advisory: [
      "Outdoor activities should be minimised",
      "Schools and offices often shift to protective measures",
      "Indoor air protection becomes critical",
    ],
  };
}

/* ---------------- COMPONENT ---------------- */

export default function GovtAirAdvisory({ aqi }) {
  const grap = getGrapStage(aqi);

  // 🚫 Do not render if AQI not available
  if (!grap) return null;

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold mb-2">
            Government Air Quality Advisory
          </h3>
          <p className="text-gray-600 text-sm">
            Based on live AQI classification & CPCB / GRAP guidelines
          </p>
        </div>

        {/* Advisory Card */}
        <div className="border rounded-2xl p-6 shadow-sm bg-gray-50">
          {/* Status */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div
              className={`px-4 py-2 rounded-full text-sm font-medium ${grap.color}`}
            >
              AQI Level: {grap.level}
            </div>

            <div className="text-sm font-semibold text-gray-700">
              {grap.stage}
            </div>
          </div>

          {/* Advisory Text */}
          <div className="space-y-3 text-sm text-gray-700">
            {grap.advisory.map((line, i) => (
              <div key={i} className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-[var(--aerzen-blue)] mt-0.5" />
                <span>{line}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t text-xs text-gray-500 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5" />
            <p>
              Advisory is indicative and based on CPCB / GRAP frameworks.
              Refer to official government notifications for enforcement actions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
